// @steered AudibleSecurityContext 2026-05-04 2026-07-08
import React, { useState, useEffect } from 'react';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import './BrowserPreview.css';

interface ValidationInfo {
  customerType?: string;
  timerEndTime: number | null;
  timerRemaining: number;
  notificationEnabled: boolean;
  onToggleNotification: () => void;
  onCopyUrl: () => void;
  urlCopied: boolean;
}

interface BrowserPreviewProps {
  tabColor?: string;
  tabName?: string;
  /** Optional initial URL for validation - when provided, browser opens with this URL pre-loaded */
  initialUrl?: string;
  /** Optional validation info to show timer and notes in the browser header */
  validationInfo?: ValidationInfo;
  /** Callback when fullscreen state changes */
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

const BrowserPreview: React.FC<BrowserPreviewProps> = ({ 
  tabColor = '#6366f1', 
  tabName = 'Browser',
  initialUrl,
  validationInfo,
  onFullscreenChange
}) => {
  // Use initialUrl if provided, otherwise default to example.com
  const defaultUrl = initialUrl || 'https://www.example.com';
  const [url, setUrl] = useState(defaultUrl);
  const [inputUrl, setInputUrl] = useState(defaultUrl);
  const [isMinimized, setIsMinimized] = useState(false);
  const [width, setWidth] = useState(40); // percentage
  const [isResizing, setIsResizing] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showValidationInfo, setShowValidationInfo] = useState(true);
  const [iframeKey, setIframeKey] = useState(0); // Used to force iframe refresh
  const [isFullscreen, setIsFullscreen] = useState(false);
  const startXRef = React.useRef(0);
  const startWidthRef = React.useRef(40);

  // Format remaining time as MM:SS
  const formatTimeRemaining = (ms: number): string => {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleRefresh = () => {
    setIframeKey(prev => prev + 1);
  };

  const toggleFullscreen = () => {
    const newState = !isFullscreen;
    setIsFullscreen(newState);
    onFullscreenChange?.(newState);
  };

  const handleNavigate = () => {
    let finalUrl = inputUrl.trim();
    
    // Add https:// if no protocol is specified
    if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      finalUrl = 'https://' + finalUrl;
    }
    
    setUrl(finalUrl);
    setInputUrl(finalUrl);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNavigate();
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      e.preventDefault();
      const containerWidth = document.querySelector('.chat-with-browser-container')?.clientWidth || window.innerWidth;
      const deltaX = startXRef.current - e.clientX;
      const deltaPercent = (deltaX / containerWidth) * 100;
      const newWidth = startWidthRef.current + deltaPercent;
      
      if (newWidth >= 25 && newWidth <= 65) {
        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  return (
    <>
      {isMinimized && (
        <div className="browser-minimized" onClick={() => setIsMinimized(false)} style={{ borderColor: tabColor }}>
          <div className="browser-minimized-icon" style={{ background: tabColor }}>🌎</div>
          <div className="browser-minimized-info">
            <span className="browser-minimized-title">{tabName}</span>
            <span className="browser-minimized-url">{new URL(url).hostname}</span>
          </div>
        </div>
      )}
      
      {!isMinimized && (
        <div className={`browser-sidebar ${isClosing ? 'closing' : ''} ${isFullscreen ? 'fullscreen' : ''}`} style={{ width: isFullscreen ? '100%' : `${width}%` }}>
          {!isFullscreen && <div className="browser-resize-handle" onMouseDown={handleMouseDown}></div>}
          {isResizing && <div className="browser-resize-overlay"></div>}
          <div className="browser-sidebar-header" style={{ borderBottomColor: tabColor }}>
            <div className="browser-sidebar-title-wrapper">
              <div className="browser-sidebar-color-bar" style={{ backgroundColor: tabColor }} />
              <span className="browser-sidebar-title">🌎 {tabName}</span>
            </div>
            <div className="browser-header-actions">
              <button 
                onClick={toggleFullscreen} 
                className="browser-sidebar-fullscreen" 
                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              </button>
              {!isFullscreen && (
                <button onClick={() => {
                  setIsClosing(true);
                  setTimeout(() => {
                    setIsMinimized(true);
                    setIsClosing(false);
                  }, 400);
                }} className="browser-sidebar-close" title="Minimize">
                  ✕
                </button>
              )}
            </div>
          </div>
          
          {/* Validation Info Panel - collapsible */}
          {validationInfo && (
            <div className={`browser-validation-panel ${showValidationInfo ? 'expanded' : 'collapsed'}`}>
              <button 
                className="browser-validation-toggle"
                onClick={() => setShowValidationInfo(!showValidationInfo)}
              >
                <span className="validation-toggle-icon">{showValidationInfo ? '▼' : '▶'}</span>
                <span className="validation-toggle-text">
                  {validationInfo.timerEndTime && validationInfo.timerRemaining > 0 
                    ? `⏱️ ${formatTimeRemaining(validationInfo.timerRemaining)} remaining`
                    : '✅ Ready to verify'
                  }
                </span>
              </button>
              
              {showValidationInfo && (
                <div className="browser-validation-content">
                  <div className="browser-validation-notes">
                    <div className="validation-note-row">
                      <div className="validation-note">
                        <strong>URL Parameter</strong>
                        The <code>g2s2StageVersionOverride</code> and <code>weblabOverride</code> parameters are required to see your changes, but won't carry forward when you navigate. Copy the full URL if needed.
                      </div>
                      <button 
                        className={`browser-copy-btn ${validationInfo.urlCopied ? 'copied' : ''}`}
                        onClick={validationInfo.onCopyUrl}
                      >
                        {validationInfo.urlCopied ? '✓ Copied' : '📋 Copy URL'}
                      </button>
                    </div>
                    {validationInfo.customerType && validationInfo.customerType !== 'anonymous' && (
                      <div className="validation-note">
                        <strong>Sign-in Required</strong>
                        Since you selected "{validationInfo.customerType}" customer type, you'll need to sign in on Audible and manually add the URL parameter to see your changes.
                      </div>
                    )}
                    <div className="validation-note-row">
                      <div className="validation-note">
                        <strong>Propagation Delay</strong>
                        G2S2 changes typically take ~15 minutes to propagate. You can come back later to verify.
                      </div>
                      {validationInfo.timerEndTime && validationInfo.timerRemaining > 0 && (
                        <button 
                          className={`browser-notify-btn ${validationInfo.notificationEnabled ? 'enabled' : ''}`}
                          onClick={validationInfo.onToggleNotification}
                        >
                          {validationInfo.notificationEnabled ? '🔔 Notify On' : '🔕 Notify Me'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="browser-sidebar-address">
            <span className="browser-lock">🔒</span>
            <input
              type="text"
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              onKeyPress={handleKeyPress}
              className="browser-url-input"
              placeholder="Enter URL..."
            />
            <button onClick={handleRefresh} className="browser-refresh-btn" title="Refresh">↻</button>
            <button onClick={handleNavigate} className="browser-go-btn" title="Go">→</button>
          </div>
          <div className="browser-sidebar-content">
            <iframe
              key={iframeKey}
              src={url}
              title="Browser Preview"
              className="browser-iframe"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default BrowserPreview;
